import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  canManageCustomerService,
  type CustomerMailDto,
  type MailCapability,
  type MailInventoryDto,
  type MailWorkspaceDomainDto,
  type MailWorkspaceMailboxDto,
  type PortalSubscriptionDto,
  type SubscriptionDto,
} from '@cpm/shared';
import { api } from '../api/client.js';
import { useMe } from '../api/auth.js';
import { Modal } from './Modal.js';
import {
  AliasForm,
  DomainForm,
  ForwarderForm,
  MailboxForm,
  MailboxPasswordReset,
  DeliverabilityModal,
} from './mail-panels.js';
import { CustomerMailDomainForm, MailDomainActionForm } from './mail-domain-forms.js';
import { CatchAllForm, MailPreferencesPanel } from './mail-preferences.js';
import { mailBytes } from './mail-summary.js';
import { timeAgo } from './charts.js';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ErrorNote,
  Input,
  Label,
  MeterBar,
  Select,
  Spinner,
  Tabs,
} from './ui.js';
function Reason({ action }: { action: MailCapability }) {
  return action.reason ? <p className="text-sm text-slate-500">{action.reason}</p> : null;
}
function Measurement({ box }: { box: MailWorkspaceMailboxDto }) {
  return (
    <>
      <p>
        {mailBytes(box.usedBytes)} · {box.quotaMb === null ? 'Unlimited' : `${box.quotaMb} MB`}{' '}
        quota
      </p>
      {box.usedBytes !== null && box.quotaMb !== null && (
        <MeterBar value={box.usedBytes} max={box.quotaMb * 1024 ** 2} />
      )}
      <p className="text-xs text-slate-500">
        {box.usageCheckedAt
          ? `Measured ${timeAgo(box.usageCheckedAt)}`
          : box.usedBytes === null
            ? 'Not measured yet'
            : 'Measurement time unavailable'}
        {box.usageRefreshFailed
          ? ' · Last refresh failed; showing the last successful sample.'
          : ''}
      </p>
    </>
  );
}
function MailboxEditor({
  box,
  domain,
  portal,
  onClose,
}: {
  box: MailWorkspaceMailboxDto;
  domain: MailWorkspaceDomainDto;
  portal: boolean;
  onClose: () => void;
}) {
  const [tab, setTab] = useState('general');
  const [name, setName] = useState(box.displayName ?? '');
  const [quota, setQuota] = useState(box.quotaMb === null ? '' : String(box.quotaMb));
  const [lowerConfirmed, setLowerConfirmed] = useState(false);
  const [login, setLogin] = useState(box.loginEnabled ?? true);
  const [delivery, setDelivery] = useState(box.deliveryEnabled ?? true);
  const [confirmation, setConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteAddress, setDeleteAddress] = useState('');
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ['mail'] });
  const save = useMutation({
    mutationFn: (body: object) => api.patch(`/api/mail/mailboxes/${box.id}`, body),
    onSuccess: refresh,
  });
  const access = useMutation({
    mutationFn: () =>
      api.patch(`/api/mail/mailboxes/${box.id}/access`, {
        loginEnabled: login,
        deliveryEnabled: delivery,
        ...(!delivery ? { confirmAddress: confirmation } : {}),
      }),
    onSettled: refresh,
  });
  const usage = useMutation({
    mutationFn: () => api.post(`/api/mail/mailboxes/${box.id}/usage/refresh`, {}),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: () =>
      api.delete(`/api/mail/mailboxes/${box.id}`, { confirmAddress: deleteAddress }),
    onSuccess: () => {
      void refresh();
      onClose();
    },
  });
  const lowering =
    quota !== '' && box.usedBytes !== null && Number(quota) * 1024 ** 2 < box.usedBytes;
  return (
    <Modal title={box.address} open onClose={onClose} wide>
      <div className="space-y-4">
        <Tabs
          tabs={[
            ['general', 'General'],
            ['storage', 'Storage'],
            ['password', 'Password'],
            ['access', 'Access & delivery'],
            ['preferences', 'Preferences'],
          ].map(([key, label]) => ({ key: key!, label: label! }))}
          active={tab}
          onChange={setTab}
        />
        <Reason action={box.capabilities.manage} />
        {box.accessPending && (
          <p role="status">Access changes are pending upstream verification.</p>
        )}
        {tab === 'general' && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate({ displayName: name.trim() || null });
            }}
          >
            <Label htmlFor="workspace-name">Display name</Label>
            <Input
              id="workspace-name"
              readOnly={!box.capabilities.manage.allowed}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <Button disabled={!box.capabilities.manage.allowed} loading={save.isPending}>
              Save name
            </Button>
          </form>
        )}
        {tab === 'storage' && (
          <div className="space-y-4">
            <Measurement box={box} />
            <Button
              variant="secondary"
              loading={usage.isPending}
              disabled={box.setupStatus !== 'ready'}
              onClick={() => usage.mutate()}
            >
              Refresh usage
            </Button>
            <ErrorNote error={usage.error} />
            <form
              className="space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                save.mutate({ quotaMb: quota === '' ? null : Number(quota) });
              }}
            >
              <Label htmlFor="workspace-quota">Mailbox quota (MB)</Label>
              <Input
                id="workspace-quota"
                type="number"
                min={1}
                max={domain.quotaCeilingMb ?? undefined}
                required={domain.quotaCeilingMb !== null || quota !== ''}
                disabled={
                  !box.capabilities.manage.allowed ||
                  (quota === '' && domain.quotaCeilingMb === null)
                }
                value={quota}
                onChange={(e) => {
                  setQuota(e.target.value);
                  setLowerConfirmed(false);
                }}
              />
              {domain.quotaCeilingMb === null ? (
                <label className="flex gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={quota === ''}
                    disabled={!box.capabilities.manage.allowed}
                    onChange={(e) => setQuota(e.target.checked ? '' : '1024')}
                  />
                  Unlimited
                </label>
              ) : (
                <p className="text-sm">
                  Plan ceiling: {domain.quotaCeilingMb} MB.
                  {box.quotaMb === null
                    ? ' The existing unlimited allocation is retained until you change it.'
                    : ''}
                </p>
              )}
              {lowering && (
                <label className="flex gap-2 text-sm text-amber-700">
                  <input
                    type="checkbox"
                    checked={lowerConfirmed}
                    onChange={(e) => setLowerConfirmed(e.target.checked)}
                  />
                  I understand the new quota is below measured usage and may prevent new mail being
                  stored.
                </label>
              )}
              <Button
                disabled={!box.capabilities.manage.allowed || (lowering && !lowerConfirmed)}
                loading={save.isPending}
              >
                Save quota
              </Button>
            </form>
          </div>
        )}
        {tab === 'password' &&
          (box.capabilities.manage.allowed ? (
            <MailboxPasswordReset mailbox={box} onDone={() => setTab('general')} />
          ) : (
            <Reason action={box.capabilities.manage} />
          ))}
        {tab === 'access' && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              access.mutate();
            }}
          >
            <label className="flex gap-2">
              <input
                type="checkbox"
                disabled={!box.capabilities.manage.allowed}
                checked={login}
                onChange={(e) => setLogin(e.target.checked)}
              />
              Allow mailbox login
            </label>
            <p className="text-sm text-slate-500">
              Controls mail apps and webmail. Subscription suspension overrides login; reactivation
              preserves this choice.
            </p>
            <label className="flex gap-2">
              <input
                type="checkbox"
                disabled={!box.capabilities.manage.allowed}
                checked={delivery}
                onChange={(e) => setDelivery(e.target.checked)}
              />
              Accept incoming delivery
            </label>
            <p className="text-sm text-slate-500">
              Stored messages are retained. Login and incoming delivery can be controlled
              independently.
            </p>
            {!delivery && (
              <div className="space-y-2 rounded-lg border border-red-300 p-3">
                <p className="text-sm">
                  Incoming messages, including mail through aliases and catch-all routing, will fail
                  permanently. Senders receive delivery failures. Rejected mail cannot be recovered
                  by enabling delivery later.
                </p>
                <Label htmlFor="confirm-block-delivery">Type {box.address} to confirm</Label>
                <Input
                  id="confirm-block-delivery"
                  value={confirmation}
                  onChange={(e) => setConfirmation(e.target.value)}
                />
              </div>
            )}
            <ErrorNote error={access.error} />
            {access.isSuccess && (
              <p role="status" className="text-sm text-emerald-700">
                Access settings saved and verified.
              </p>
            )}
            <Button
              disabled={
                !box.capabilities.manage.allowed || (!delivery && confirmation !== box.address)
              }
              loading={access.isPending}
            >
              Save access & delivery
            </Button>
          </form>
        )}
        {tab === 'preferences' &&
          (box.capabilities.preferences.allowed ? (
            <MailPreferencesPanel mailboxId={box.id} customer={portal} />
          ) : (
            <Reason action={box.capabilities.preferences} />
          ))}
        <ErrorNote error={save.error} />
        {save.isSuccess && (
          <p role="status" className="text-sm text-emerald-700">
            Mailbox saved.
          </p>
        )}
        {tab === 'general' && (
          <div className="space-y-3 border-t pt-4">
            <Reason action={box.capabilities.delete} />
            {!deleting ? (
              <Button
                variant="danger"
                disabled={!box.capabilities.delete.allowed}
                onClick={() => setDeleting(true)}
              >
                Delete mailbox
              </Button>
            ) : (
              <form
                className="space-y-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  remove.mutate();
                }}
              >
                <p>
                  Deleting removes the mailbox and its stored messages permanently. Reassign any
                  catch-all target first.
                </p>
                <Label htmlFor="confirm-delete-mailbox">
                  Type {box.address} to confirm deletion
                </Label>
                <Input
                  id="confirm-delete-mailbox"
                  value={deleteAddress}
                  onChange={(e) => setDeleteAddress(e.target.value)}
                />
                <ErrorNote error={remove.error} />
                <Button
                  variant="danger"
                  disabled={deleteAddress !== box.address}
                  loading={remove.isPending}
                >
                  Permanently delete
                </Button>
              </form>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
function AddDomain({
  portal,
  subscriptionId,
  onClose,
}: {
  portal: boolean;
  subscriptionId: string;
  onClose: () => void;
}) {
  const [selected, setSelected] = useState(subscriptionId);
  const subs = useQuery({
    queryKey: ['mail', 'subscription-choices', portal],
    queryFn: () =>
      api.get<Array<PortalSubscriptionDto | SubscriptionDto>>(
        portal ? '/api/portal/subscriptions' : '/api/subscriptions',
      ),
  });
  const options = useQuery({
    queryKey: ['mail', 'subscription', selected],
    queryFn: () => api.get<CustomerMailDto>(`/api/mail/subscriptions/${selected}`),
    enabled: !!selected && portal,
    retry: false,
  });
  return (
    <Modal title="Add mail domain" open onClose={onClose}>
      <div className="space-y-4">
        <Label htmlFor="mail-subscription">Subscription</Label>
        <Select
          id="mail-subscription"
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
        >
          <option value="">Choose a subscription</option>
          {subs.data
            ?.filter((s) => s.status === 'active')
            .map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
        </Select>
        <ErrorNote error={subs.error} />
        {!!selected &&
          (!portal ? (
            <DomainForm key={selected} subscriptionId={selected} onDone={onClose} />
          ) : options.isPending ? (
            <Spinner />
          ) : options.error ? (
            <ErrorNote error={options.error} />
          ) : options.data?.canManageDomains ? (
            <CustomerMailDomainForm
              key={selected}
              subscriptionId={selected}
              domains={options.data.availableDomains ?? []}
              onDone={onClose}
            />
          ) : (
            <p>Your effective plan does not include domain management.</p>
          ))}
      </div>
    </Modal>
  );
}
export function MailWorkspace({ portal = false }: { portal?: boolean }) {
  const [params, setParams] = useSearchParams();
  const me = useMe();
  const qc = useQueryClient();
  const domainId = params.get('domainId') ?? '';
  const subscriptionId = params.get('subscriptionId') ?? '';
  const mailboxId = params.get('mailboxId') ?? '';
  const tab = params.get('domainTab') ?? 'mailboxes';
  const [mailboxSearch, setMailboxSearch] = useState('');
  const [modal, setModal] = useState('');
  const [aliasBox, setAliasBox] = useState('');
  const [forwarderId, setForwarderId] = useState('');
  const filter = new URLSearchParams({
    pageSize: '25',
    page: params.get('page') ?? '1',
    search: params.get('search') ?? '',
  });
  for (const key of ['subscriptionId', 'clientId']) {
    const value = params.get(key);
    if (value) filter.set(key, value);
  }
  const data = useQuery({
    queryKey: ['mail', 'domains', filter.toString()],
    queryFn: () => api.get<MailInventoryDto<MailWorkspaceDomainDto>>(`/api/mail/domains?${filter}`),
  });
  const subscriptions = useQuery({
    queryKey: ['mail', 'subscription-filters', portal],
    queryFn: () =>
      api.get<Array<PortalSubscriptionDto | SubscriptionDto>>(
        portal ? '/api/portal/subscriptions' : '/api/subscriptions',
      ),
    enabled: !domainId,
  });
  const detail = useQuery({
    queryKey: ['mail', 'domain', domainId],
    queryFn: () =>
      api.get<MailInventoryDto<MailWorkspaceDomainDto>>(`/api/mail/domains?domainId=${domainId}`),
    enabled: !!domainId,
  });
  const boxDetail = useQuery({
    queryKey: ['mail', 'mailbox', mailboxId],
    queryFn: () => api.get<MailWorkspaceMailboxDto>(`/api/mail/mailboxes/${mailboxId}`),
    enabled: !!mailboxId,
  });
  useEffect(() => {
    if (mailboxId && !domainId && boxDetail.data) {
      const next = new URLSearchParams(params);
      next.set('domainId', boxDetail.data.domainId);
      setParams(next, { replace: true });
    }
  }, [mailboxId, domainId, boxDetail.data, params, setParams]);
  const selected = detail.data?.items[0];
  const change = (values: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(values)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setParams(next);
  };
  const close = () => setModal('');
  const retrySetup = useMutation({
    mutationFn: () => api.post(`/api/mail/domains/${domainId}/retry-provisioning`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mail'] }),
  });
  const remove = useMutation({
    mutationFn: (path: string) => api.delete(path),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mail'] }),
  });
  const canAdd = me.data && (!portal || canManageCustomerService(me.data, 'mail'));
  return (
    <div className="space-y-4">
      {!domainId ? (
        <>
          <div className="flex flex-wrap items-end gap-3">
            <form
              className="flex flex-1 gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                const value = new FormData(e.currentTarget).get('search');
                change({ search: String(value ?? ''), page: null });
              }}
            >
              <div className="flex-1">
                <Label htmlFor="search-mail">
                  Search domains, subscriptions{!portal ? ', or customers' : ''}
                </Label>
                <Input
                  key={params.get('search')}
                  id="search-mail"
                  name="search"
                  defaultValue={params.get('search') ?? ''}
                />
              </div>
              <Button className="self-end" variant="secondary">
                Search
              </Button>
            </form>
            {canAdd && <Button onClick={() => setModal('domain')}>Add domain</Button>}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="mail-subscription-filter">Subscription filter</Label>
              <Select
                id="mail-subscription-filter"
                value={subscriptionId}
                onChange={(e) => change({ subscriptionId: e.target.value, page: null })}
              >
                <option value="">All subscriptions</option>
                {subscriptions.data?.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            </div>
            {!portal && (
              <div>
                <Label htmlFor="mail-customer-filter">Customer filter</Label>
                <Select
                  id="mail-customer-filter"
                  value={params.get('clientId') ?? ''}
                  onChange={(e) =>
                    change({ clientId: e.target.value, subscriptionId: null, page: null })
                  }
                >
                  <option value="">All customers</option>
                  {Array.from(
                    new Map(
                      (subscriptions.data as SubscriptionDto[] | undefined)?.map((s) => [
                        s.clientId,
                        s.clientName,
                      ]) ?? [],
                    ),
                  ).map(([id, name]) => (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  ))}
                </Select>
              </div>
            )}
          </div>
          {(subscriptionId || params.get('clientId')) && (
            <p className="text-sm">
              Filtered by {subscriptionId ? 'subscription' : 'customer'}.{' '}
              <button
                className="text-brand-600"
                onClick={() => change({ subscriptionId: null, clientId: null, page: null })}
              >
                Clear filter
              </button>
            </p>
          )}
          {data.isPending ? (
            <Spinner />
          ) : data.error ? (
            <ErrorNote error={data.error} />
          ) : (
            <>
              <div className="grid gap-3">
                {data.data.items.map((d) => (
                  <Card key={d.id}>
                    <CardHeader
                      title={
                        <Link
                          className="text-brand-600"
                          to={`?${new URLSearchParams({ ...Object.fromEntries(params), domainId: d.id })}`}
                        >
                          {d.domain}
                        </Link>
                      }
                      description={`${d.mailboxes.length} mailboxes · ${mailBytes(d.knownUsedBytes)} known storage${d.unknownUsageCount ? ' (partial total)' : ''}`}
                      actions={
                        <Badge tone={d.setupStatus === 'ready' ? 'green' : 'yellow'}>
                          {d.setupStatus === 'ready' ? 'Ready' : 'Needs attention'}
                        </Badge>
                      }
                    />
                    <div className="px-5 pb-4 text-sm text-slate-500">
                      {d.subscriptionName}
                      {d.clientName ? ` · ${d.clientName}` : ''} · {d.status} · DNS{' '}
                      {d.publicationStatus === 'enabled'
                        ? 'publication enabled'
                        : 'awaiting provider review'}
                    </div>
                  </Card>
                ))}
              </div>
              {!data.data.total && <p>No mail domains match this view.</p>}
              <div className="flex items-center gap-3">
                <Button
                  variant="secondary"
                  disabled={data.data.page <= 1}
                  onClick={() => change({ page: String(data.data.page - 1) })}
                >
                  Previous
                </Button>
                <p className="text-sm">
                  Page {data.data.page} · {data.data.total} domains
                </p>
                <Button
                  variant="secondary"
                  disabled={data.data.page * data.data.pageSize >= data.data.total}
                  onClick={() => change({ page: String(data.data.page + 1) })}
                >
                  Next
                </Button>
              </div>
            </>
          )}
        </>
      ) : detail.isPending ? (
        <Spinner />
      ) : detail.error ? (
        <ErrorNote error={detail.error} />
      ) : (
        selected && (
          <>
            <Button
              variant="ghost"
              onClick={() => change({ domainId: null, mailboxId: null, domainTab: null })}
            >
              ← Domains
            </Button>
            <h2 className="text-xl font-semibold">{selected.domain}</h2>
            <p className="text-sm text-slate-500">
              {selected.subscriptionName}
              {selected.clientName ? ` · ${selected.clientName}` : ''}
            </p>
            <Tabs
              tabs={[
                { key: 'mailboxes', label: 'Mailboxes' },
                { key: 'routing', label: 'Routing' },
                { key: 'settings', label: 'Domain settings' },
              ]}
              active={tab}
              onChange={(value) => change({ domainTab: value })}
            />
            <Reason action={selected.capabilities.manage} />
            {tab === 'mailboxes' && (
              <div className="space-y-4">
                <Button
                  disabled={!selected.capabilities.manage.allowed}
                  onClick={() => setModal('mailbox')}
                >
                  Create mailbox
                </Button>
                {!selected.mailboxes.length && <p>No mailboxes yet.</p>}
                <div>
                  <Label htmlFor="mailbox-search">Search mailboxes</Label>
                  <Input
                    id="mailbox-search"
                    value={mailboxSearch}
                    onChange={(e) => setMailboxSearch(e.target.value)}
                  />
                </div>
                {selected.mailboxes
                  .filter((box) =>
                    `${box.address} ${box.displayName ?? ''}`
                      .toLowerCase()
                      .includes(mailboxSearch.toLowerCase()),
                  )
                  .map((box) => (
                    <Card key={box.id}>
                      <CardHeader
                        title={box.address}
                        description={box.displayName ?? undefined}
                        actions={
                          <Button variant="secondary" onClick={() => change({ mailboxId: box.id })}>
                            Manage
                          </Button>
                        }
                      />
                      <div className="space-y-2 px-5 pb-4 text-sm">
                        <Measurement box={box} />
                        <p>
                          Login:{' '}
                          {box.setupStatus !== 'ready'
                            ? 'unverified (setup needs attention)'
                            : box.status === 'active'
                              ? 'enabled'
                              : 'blocked'}{' '}
                          · Incoming delivery:{' '}
                          {box.setupStatus !== 'ready'
                            ? 'unverified'
                            : box.deliveryEnabled === false
                              ? 'blocked'
                              : 'enabled'}
                          {box.accessPending ? ' · Synchronization pending' : ''}
                        </p>
                      </div>
                    </Card>
                  ))}
              </div>
            )}
            {tab === 'routing' && (
              <div className="space-y-4">
                <Card>
                  <CardHeader title="Aliases" />
                  <div className="space-y-3 px-5 pb-4">
                    {selected.mailboxes.map((box) => (
                      <div key={box.id} className="space-y-2">
                        <p>{box.address}</p>
                        {box.aliases.map((a) => (
                          <div key={a.id} className="flex flex-wrap items-center gap-3 text-sm">
                            <span>{a.address}</span>
                            <Button
                              variant="ghost"
                              disabled={!box.capabilities.manage.allowed}
                              onClick={() => {
                                if (window.confirm(`Remove ${a.address}?`))
                                  remove.mutate(`/api/mail/aliases/${a.id}`);
                              }}
                            >
                              Remove
                            </Button>
                          </div>
                        ))}
                        <Button
                          variant="secondary"
                          disabled={!box.capabilities.manage.allowed}
                          onClick={() => {
                            setAliasBox(box.id);
                            setModal('alias');
                          }}
                        >
                          Add alias
                        </Button>
                      </div>
                    ))}
                  </div>
                </Card>
                <Card>
                  <CardHeader
                    title="Standalone forwarders"
                    actions={
                      <Button
                        disabled={!selected.capabilities.manage.allowed}
                        onClick={() => {
                          setForwarderId('');
                          setModal('forwarder');
                        }}
                      >
                        Add forwarder
                      </Button>
                    }
                  />
                  <div className="space-y-3 px-5 pb-4">
                    {selected.forwarders.map((f) => (
                      <div className="flex flex-wrap items-center gap-3 text-sm" key={f.id}>
                        <span className="flex-1">
                          {f.address} → {f.recipients.join(', ')}
                        </span>
                        <Button
                          variant="secondary"
                          disabled={!selected.capabilities.manage.allowed}
                          onClick={() => {
                            setForwarderId(f.id);
                            setModal('forwarder');
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="danger"
                          disabled={
                            me.data?.role === 'staff' || !selected.capabilities.manage.allowed
                          }
                          onClick={() => {
                            if (window.confirm(`Delete ${f.address}?`))
                              remove.mutate(`/api/mail/forwarders/${f.id}`);
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    ))}
                  </div>
                </Card>
                <Card>
                  <CardHeader
                    title="Catch-all delivery"
                    description={selected.catchAllAddress ?? 'Disabled'}
                  />
                  <div className="px-5 pb-4">
                    <Reason action={selected.capabilities.preferences} />
                    <Button
                      disabled={!selected.capabilities.preferences.allowed}
                      onClick={() => setModal('catchall')}
                    >
                      Change catch-all
                    </Button>
                  </div>
                </Card>
                <ErrorNote error={remove.error} />
              </div>
            )}
            {tab === 'settings' && (
              <Card>
                <CardHeader
                  title="Domain settings"
                  description={`${selected.setupStatus} · DNS ${selected.publicationStatus}`}
                />
                <div className="space-y-4 px-5 pb-4">
                  {!portal && ['admin', 'staff'].includes(me.data?.role ?? '') && (
                    <Button
                      variant="secondary"
                      disabled={!selected.capabilities.manage.allowed}
                      onClick={() => setModal('deliverability')}
                    >
                      DNS & deliverability
                    </Button>
                  )}
                  {selected.setupStatus === 'needs-attention' && (
                    <div className="space-y-2">
                      <Reason action={selected.capabilities.retrySetup} />
                      {selected.capabilities.retrySetup.allowed && (
                        <Button
                          loading={retrySetup.isPending}
                          onClick={() => {
                            if (
                              window.confirm(
                                `Retry setup for ${selected.domain}? This links an existing server domain or creates it if absent. Its saved DNS publication setting is preserved.`,
                              )
                            )
                              retrySetup.mutate();
                          }}
                        >
                          Retry setup
                        </Button>
                      )}
                      <ErrorNote error={retrySetup.error} />
                    </div>
                  )}
                  <Reason action={selected.capabilities.publishDns} />
                  {selected.capabilities.publishDns.allowed && (
                    <Button onClick={() => setModal('publish')}>Enable DNS publication</Button>
                  )}
                  <Reason action={selected.capabilities.delete} />
                  <Button
                    variant="danger"
                    disabled={!selected.capabilities.delete.allowed}
                    onClick={() => setModal('remove')}
                  >
                    Remove domain
                  </Button>
                </div>
              </Card>
            )}
            {modal === 'mailbox' && (
              <Modal title={`Create mailbox — ${selected.domain}`} open onClose={close}>
                <MailboxForm
                  domain={selected}
                  quotaCeilingMb={selected.quotaCeilingMb}
                  onDone={close}
                />
              </Modal>
            )}
            {modal === 'alias' && selected.mailboxes.find((b) => b.id === aliasBox) && (
              <Modal title="Add alias" open onClose={close}>
                <AliasForm
                  mailbox={selected.mailboxes.find((b) => b.id === aliasBox)!}
                  onDone={close}
                />
              </Modal>
            )}
            {modal === 'forwarder' && (
              <Modal
                title={forwarderId ? 'Edit forwarder' : 'Create forwarder'}
                open
                onClose={close}
              >
                <ForwarderForm
                  domain={selected}
                  existing={selected.forwarders.find((f) => f.id === forwarderId)}
                  onDone={close}
                />
              </Modal>
            )}
            {modal === 'catchall' && (
              <Modal title="Catch-all delivery" open onClose={close}>
                <CatchAllForm domain={selected} customer={portal} onDone={close} />
              </Modal>
            )}
            {(modal === 'publish' || modal === 'remove') && (
              <Modal
                title={modal === 'publish' ? 'Enable DNS publication' : 'Remove domain'}
                open
                onClose={close}
              >
                <MailDomainActionForm
                  domain={selected}
                  action={modal}
                  onDone={() => {
                    close();
                    if (modal === 'remove') change({ domainId: null });
                  }}
                />
              </Modal>
            )}
            {modal === 'deliverability' && (
              <DeliverabilityModal
                domain={selected}
                isAdmin={me.data?.role === 'admin'}
                open
                onClose={close}
              />
            )}
          </>
        )
      )}
      {modal === 'domain' && (
        <AddDomain portal={portal} subscriptionId={subscriptionId} onClose={close} />
      )}
      {!!mailboxId &&
        (boxDetail.isPending ? (
          <Spinner />
        ) : boxDetail.error ? (
          <ErrorNote error={boxDetail.error} />
        ) : selected ? (
          <MailboxEditor
            key={`${boxDetail.data.id}:${boxDetail.data.accessPending}`}
            box={boxDetail.data}
            domain={selected}
            portal={portal}
            onClose={() => change({ mailboxId: null })}
          />
        ) : null)}
    </div>
  );
}
